// helpDesk.ts · v12.10.30 (#2259 Help & Support) · 2026-11-03 · VoiceSpectrum AI
// v12.10.30 (#2259): the Help & Support page's data - My tickets (GET/POST /api/t2/help-desk/...; the same store Support Ops ›
// Support requests reads) and What's new (/whats-new.json, written into the T2 site by Package-v12.ps1 from the backlog's
// fixedVer at package time). "Seen" (ticket updates, the newest release) is kept per person in this browser (localStorage).
import { api } from './api';

// v12.10.30 (#2259): Tushar 11/3 - the caption is still being confirmed ("Help & Support" proposed). ONE place to change it.
export const HELP_SUPPORT_CAPTION = 'Help desk';   // v12.10.30 (#2259, Tushar 11/3 ~6:45 PM): "let;s keep help desk which is short and tells there is more than help in this section"
/** The account menu's entry (bottom-left) - opens the page on My tickets. */
export const HELP_DESK_MENU_LABEL = 'Help desk';
/** The page's address; ?tab=tickets | news (no tab = Help). */
export const HELP_SUPPORT_PATH = '/guides';
export type HelpSupportTab = 'help' | 'tickets' | 'news';
export const helpSupportUrl = (tab: HelpSupportTab = 'help', ticketId?: number) =>
  `${HELP_SUPPORT_PATH}${tab === 'help' ? '' : `?tab=${tab}${ticketId ? `&t=${ticketId}` : ''}`}`;

export type TicketStatus = 'open' | 'in_progress' | 'answered' | 'closed';
export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = { open: 'Open', in_progress: 'In progress', answered: 'Answered', closed: 'Closed' };

export interface TicketTurn { id: number; at: string; body: string; from: 'practice' | 'support'; who: string; mine: boolean }
export interface Ticket {
  id: number; reference: string; subject: string; status: TicketStatus;
  createdAt: string; lastUpdate: string; supportUpdateAt?: string | null;
  name?: string | null; contact: string; mine: boolean;
  message?: string | null; thread?: TicketTurn[] | null;
}
export interface HelpDeskMe { name: string; email: string; practice: string; all: boolean; support: boolean }
export interface TicketList { me: HelpDeskMe; items: Ticket[] }
/** The Contact support form's four fields (login/views.tsx SupportFields - the same form, signed in). */
export interface NewTicket { name: string; practice: string; contact: string; message: string }

export const helpDeskApi = {
  list: () => api.get<TicketList>('/t2/help-desk/tickets'),
  get: (id: number) => api.get<Ticket>(`/t2/help-desk/tickets/${id}`),
  create: (t: NewTicket) => api.post<{ status: string; reference: string; ticket: Ticket }>('/t2/help-desk/tickets',
    { name: t.name || null, practice: t.practice || null, contact: t.contact, message: t.message }),
  reply: (id: number, message: string) => api.post<Ticket>(`/t2/help-desk/tickets/${id}/reply`, { message }),
};

// ── What's new ──────────────────────────────────────────────────────────────────────────────────────────────────────
export interface WhatsNewItem { num?: number | string; text: string; area?: string }
export interface WhatsNewVersion { version: string; date?: string | null; items: WhatsNewItem[] }
export interface WhatsNew { generated?: string; versions: WhatsNewVersion[] }

/** /whats-new.json beside version.json; null when this build has none (a dev build, or before the first packaged build). */
export async function loadWhatsNew(): Promise<WhatsNew | null> {
  const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  try {
    const r = await fetch(`${base}whats-new.json`, { cache: 'no-store' });
    if (!r.ok) return null;
    const j = (await r.json()) as WhatsNew;
    return j && Array.isArray(j.versions) ? { ...j, versions: j.versions.filter((v) => v && v.version && Array.isArray(v.items) && v.items.length > 0) } : null;
  } catch { return null; }
}

/** 12.10.30 > 12.10.29.1 (numbers, part by part). */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const d = (pa[i] ?? 0) - (pb[i] ?? 0); if (d) return d; }
  return 0;
}

// ── "seen" per person, this browser ─────────────────────────────────────────────────────────────────────────────────
interface Seen { tickets: Record<string, string>; news?: string }
const seenKey = (who: string) => `vs_helpdesk_seen:${who.toLowerCase()}`;
export function readSeen(who: string): Seen {
  try { const s = JSON.parse(localStorage.getItem(seenKey(who)) ?? 'null') as Seen | null; return s && typeof s === 'object' ? { tickets: s.tickets ?? {}, news: s.news } : { tickets: {} }; }
  catch { return { tickets: {} }; }
}
function writeSeen(who: string, s: Seen) { try { localStorage.setItem(seenKey(who), JSON.stringify(s)); } catch { /* private window */ } }
export function markTicketSeen(who: string, t: Ticket) {
  const s = readSeen(who);
  s.tickets[String(t.id)] = t.supportUpdateAt ?? new Date().toISOString();
  writeSeen(who, s);
}
export function markNewsSeen(who: string, version: string) { const s = readSeen(who); s.news = version; writeSeen(who, s); }
/** A ticket of mine that Support answered or closed since I last opened it. */
export function ticketUnread(t: Ticket, seen: Seen): boolean {
  if (!t.mine || !t.supportUpdateAt) return false;
  const at = seen.tickets[String(t.id)];
  return !at || new Date(t.supportUpdateAt).getTime() > new Date(at).getTime();
}
export function newsUnread(news: WhatsNew | null, seen: Seen): boolean {
  const top = news?.versions[0]?.version;
  return !!top && (!seen.news || compareVersions(top, seen.news) > 0);
}
